import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, Lock, User, ArrowRight, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";

function formatErr(detail) {
  if (!detail) return "Error desconocido";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" · ");
  return String(detail);
}

export default function Register() {
  const auth = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    if (password.length < 8) { setErr("La contraseña debe tener al menos 8 caracteres"); setBusy(false); return; }
    if (!acceptedTerms || !acceptedPrivacy) { setErr("Debes aceptar los Términos y la Política de Privacidad"); setBusy(false); return; }
    try {
      await auth.register(email, password, name, { accepted_terms: true, accepted_privacy: true });
      toast.success("Cuenta creada · Bienvenido a Ovamx");
      nav("/", { replace: true });
    } catch (ex) {
      setErr(formatErr(ex?.response?.data?.detail) || ex.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="register-page" className="min-h-screen bg-cream text-ink flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Logo size={56} className="mx-auto" />
          <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.3em] text-coral">◊ Crear cuenta</p>
          <h1 className="mt-2 font-serif text-5xl tracking-tighter leading-none">Únete<br/>a Ovamx.</h1>
          <p className="mt-3 text-sm text-ink-mid">Privacidad por diseño · Tu identidad se respeta.</p>
        </div>

        <form onSubmit={submit} className="space-y-3">
          <Field icon={User} testid="register-name" type="text" placeholder="Tu nombre"
            value={name} onChange={setName} required autoFocus />
          <Field icon={Mail} testid="register-email" type="email" placeholder="email@ovamx.com"
            value={email} onChange={setEmail} required />
          <Field icon={Lock} testid="register-password" type="password" placeholder="contraseña (mín. 8 caracteres)"
            value={password} onChange={setPassword} required minLength={8} />

          {err && (
            <div data-testid="register-error" className="flex items-center gap-2 px-3 py-2 rounded-xl bg-red-50 border border-red-200 text-red-700 text-xs">
              <AlertCircle className="w-3.5 h-3.5 shrink-0" />
              <span>{err}</span>
            </div>
          )}

          <label className="flex items-start gap-2 text-xs text-ink-mid leading-relaxed cursor-pointer">
            <input type="checkbox" checked={acceptedTerms} onChange={(e) => setAcceptedTerms(e.target.checked)}
                   data-testid="register-accept-terms" className="mt-0.5 accent-coral" />
            <span>
              Acepto los <Link to="/legal/terminos" target="_blank" className="text-coral hover:underline">Términos y Condiciones</Link>
              {" y la "}
              <Link to="/legal/politica-uso" target="_blank" className="text-coral hover:underline">Política de Uso</Link>.
            </span>
          </label>
          <label className="flex items-start gap-2 text-xs text-ink-mid leading-relaxed cursor-pointer">
            <input type="checkbox" checked={acceptedPrivacy} onChange={(e) => setAcceptedPrivacy(e.target.checked)}
                   data-testid="register-accept-privacy" className="mt-0.5 accent-coral" />
            <span>
              He leído la <Link to="/legal/privacidad" target="_blank" className="text-coral hover:underline">Política de Privacidad</Link>
              {" y acepto el tratamiento de mis datos."}
            </span>
          </label>

          <button type="submit" disabled={busy || !acceptedTerms || !acceptedPrivacy}
            data-testid="register-submit"
            className="w-full inline-flex items-center justify-center gap-2 bg-coral text-white rounded-full py-3 hover:bg-coral-hover transition disabled:opacity-50">
            {busy ? "Creando…" : <>Crear cuenta <ArrowRight className="w-4 h-4" /></>}
          </button>
        </form>

        <p className="mt-6 text-center text-xs text-ink-mid">
          ¿Ya tienes cuenta? <Link to="/login" data-testid="register-to-login" className="text-coral hover:underline">Inicia sesión</Link>
        </p>
      </div>
    </div>
  );
}

function Field({ icon: Icon, testid, ...props }) {
  return (
    <div className="relative">
      <Icon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-mid" />
      <input
        data-testid={testid}
        {...props}
        onChange={(e) => props.onChange(e.target.value)}
        className="w-full bg-cream-card border hairline rounded-2xl pl-11 pr-4 py-3 text-sm outline-none focus:border-coral"
      />
    </div>
  );
}
